/**
 * Отчёт по шагам скрипта (#879): какие шаги скрипта не выполняются и почему.
 * Спека — `docs/plans/2026-10-05-script-steps-report.md`.
 *
 * Два маршрута: сводка «скрипты → шаги» и деталь одного шага (менеджеры и
 * критерии внутри него). Агрегаты — Postgres `script_adherence_results`.
 *
 * Формулировка — «не выполнен», а не «пропущен»: шаг выполнен, только когда
 * совпали ВСЕ обязательные критерии, и 68 % «невыполненных» шагов на деле
 * выполнены частично (спека §3.2). Цитаты из разговора (`evidence`) в ответ не
 * входят вовсе — отчёт читает только счётчики и тексты правил клиента.
 *
 * Ответы читаются клиентом нестрого: новое поле не ломает старый бандл.
 */
import { z } from 'zod/v4';
import { reportScopeEchoShape } from './criteria-report';
import { reportSummaryQuerySchema } from './reports';

/** Запрос обоих маршрутов — область и период те же, что у «Головне». */
export const scriptStepsReportQuerySchema = reportSummaryQuerySchema;

/** `GET /api/v1/reports/script-steps/:stepId` — `script_steps.id` из D1. */
export const scriptStepsReportParamSchema = z.object({
  stepId: z.string().min(1).max(64),
});

/** Потолок таблицы менеджеров шага; больше в одной организации не бывает. */
export const SCRIPT_STEPS_REPORT_AGENTS_LIMIT = 200;
/** Потолок критериев одного шага. */
export const SCRIPT_STEPS_REPORT_CRITERIA_LIMIT = 100;

const countSchema = z.number().int().nonnegative();

export const scriptStepsReportStepSchema = z.object({
  stepId: z.string(),
  stepNumber: z.number().int().positive(),
  /** Имя из живого скрипта; шага там уже нет (`removed`) — имя самого нового звонка. */
  name: z.string(),
  /**
   * Шага в живом скрипте больше нет (удалён или скрипт удалён). Нет поля —
   * признак не определён (скрипты не прочитались), а не «жив».
   */
  removed: z.boolean().optional(),
  /** Звонков, где шаг должен был прозвучать, — знаменатель доли. */
  owed: countSchema,
  /** Из них `isCompleted = false` — главное число. */
  notCompleted: countSchema,
  /** Из `notCompleted` — с баллом шага больше нуля. */
  partial: countSchema,
  /** Из `notCompleted` — не хватило ровно одного обязательного критерия. */
  oneCriterionShort: countSchema,
  /** «Не понадобился»: необязательный шаг не прозвучал, в знаменатель не входит. */
  notOwed: countSchema,
  /** Всегда 0 до #497 (`applies_when`): поле есть, чтобы #497 не менял контракт. */
  notApplicable: countSchema,
});

export const scriptStepsReportScriptSchema = z.object({
  scriptId: z.string(),
  /** Имя из D1; скрипта нет — пустая строка и `deleted: true`; D1 не прочиталась — пустая строка без `deleted`. */
  name: z.string(),
  /** Нет поля — признак не определён (скрипты не прочитались), а не «жив». */
  deleted: z.boolean().optional(),
  /** Звонков периода с оценкой по этому скрипту — знаменатель «оценено N». */
  scoredCalls: countSchema,
  /**
   * Версии скрипта среди посчитанных строк по возрастанию и момент первой
   * оценки каждой: больше одной — в числах смешаны разные правила.
   */
  versions: z.array(
    z.object({ version: z.number().int(), firstEvaluatedAt: z.string() })
  ),
  /** По убыванию `notCompleted`, затем доли, затем номера шага. */
  steps: z.array(scriptStepsReportStepSchema),
});

export const scriptStepsReportResponseSchema = z.object({
  ...reportScopeEchoShape,
  coverage: z.object({
    /**
     * Записи периода в области, у которых есть текущий анализ с метриками (не все
     * записи периода: без анализа и с анализом без метрик не входят) — знаменатель
     * покрытия «оценено N из M».
     */
    recordsInWindow: countSchema,
  }),
  scripts: z.array(scriptStepsReportScriptSchema),
});

export const scriptStepsReportAgentSchema = z.object({
  agentId: z.string(),
  /**
   * Установка CRM сотрудника без аккаунта Aelo (его id локален для портала),
   * иначе `null` (#1927). Строка — человек, а не `agentId`: один несвязанный id
   * в двух порталах — две строки, различаются они этим полем. Необязательное,
   * чтобы страница пережила edge-api, откатанный до релиза без него.
   */
  installationId: z.string().nullable().optional(),
  agentName: z.string().nullable(),
  owed: countSchema,
  notCompleted: countSchema,
});

export const scriptStepsReportCriterionSchema = z.object({
  phraseId: z.string(),
  /** Текст правила клиента, не разговор: сегодняшний; удалённого критерия — записанный в оценке. */
  phraseText: z.string(),
  /**
   * Критерия в живом скрипте больше нет (удалён вместе с шагом или скриптом).
   * Нет поля — признак не определён (скрипты не прочитались), а не «жив».
   */
  removed: z.boolean().optional(),
  /** В скольких звонках шага критерий не засчитан. */
  missed: countSchema,
  /**
   * Оценок, в которых критерий был: шаг должен был прозвучать, а критерий есть среди
   * недобранных или совпавших (знаменатель САМОГО критерия, не шага: версия скрипта до
   * его появления не входит). Не меньше `missed`.
   */
  owed: countSchema,
  /** Не засчитан почти всегда (`SCRIPT_CRITERION_ALMOST_NEVER_*` в `@aica/core`). */
  almostNever: z.boolean(),
  /** В скольких невыполнениях шага он был единственным недостающим. */
  soleCause: countSchema,
});

export const scriptStepsReportDetailResponseSchema = z.object({
  ...reportScopeEchoShape,
  stepId: z.string(),
  /** `null` — по шагу в периоде и области нет ни одной оценки (в том числе чужой шаг). */
  scriptId: z.string().nullable(),
  owed: countSchema,
  notCompleted: countSchema,
  /** Менеджеры с числом звонков не ниже порога; по убыванию `notCompleted`. */
  agents: z.array(scriptStepsReportAgentSchema).max(SCRIPT_STEPS_REPORT_AGENTS_LIMIT),
  /** Сколько менеджеров ниже порога звонков — сноска «ещё N с малым числом звонков». */
  agentsBelowThreshold: countSchema.optional(),
  /** Сколько менеджеров не вошло в `agents` из-за потолка таблицы — сноска «и ещё N». */
  agentsTruncated: countSchema.optional(),
  /** Звонки без менеджера — отдельной строкой, а не молча отброшенные. */
  unattributed: z.object({ owed: countSchema, notCompleted: countSchema }),
  /** Сколько критериев не вошло в `criteria` из-за потолка — сноска «и ещё N». */
  criteriaTruncated: countSchema.optional(),
  /** По убыванию `missed`. */
  criteria: z
    .array(scriptStepsReportCriterionSchema)
    .max(SCRIPT_STEPS_REPORT_CRITERIA_LIMIT),
});

export type ScriptStepsReportQuery = z.infer<typeof scriptStepsReportQuerySchema>;
export type ScriptStepsReportStep = z.infer<typeof scriptStepsReportStepSchema>;
export type ScriptStepsReportScript = z.infer<typeof scriptStepsReportScriptSchema>;
export type ScriptStepsReportResponse = z.infer<typeof scriptStepsReportResponseSchema>;
export type ScriptStepsReportAgent = z.infer<typeof scriptStepsReportAgentSchema>;
export type ScriptStepsReportCriterion = z.infer<typeof scriptStepsReportCriterionSchema>;
export type ScriptStepsReportDetailResponse = z.infer<
  typeof scriptStepsReportDetailResponseSchema
>;
